import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../store/authStore'
import { supabase } from '../lib/supabase'

export const useMembers = () => {
  const household = useAuthStore((s) => s.household)
  const user      = useAuthStore((s) => s.user)
  const qc        = useQueryClient()
  const key       = ['members', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      // Own household members
      const { data: ownMembers, error } = await supabase
        .from('household_members')
        .select('role, joined_at, user_id, profiles(id, display_name, avatar_url, member_color)')
        .eq('household_id', household.id)
        .order('joined_at')
      if (error) throw error

      // Shared children from a linked household — for calendar tagging only
      const { data: sharedMemberData } = await supabase
        .from('shared_members')
        .select('user_id, profiles(id, display_name, avatar_url, member_color)')
        .eq('linked_household_id', household.id)

      const sharedMembers = (sharedMemberData ?? []).map((sm) => ({
        user_id:    sm.user_id,
        role:       'limited',
        joined_at:  null,
        isShared:   true,
        profiles:   sm.profiles,
      }))

      return [...(ownMembers ?? []), ...sharedMembers]
    },
  })

  const invite = useMutation({
    mutationFn: async ({ email, role }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await supabase.functions.invoke('send-invite', {
        body: { email, role, household_id: household.id },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      if (res.error) throw res.error
      if (res.data?.error) throw new Error(res.data.error)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', household?.id] }),
  })

  const changeRole = useMutation({
    mutationFn: async ({ userId, role }) => {
      const { error } = await supabase
        .from('household_members')
        .update({ role })
        .eq('household_id', household.id)
        .eq('user_id', userId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async (userId) => {
      const { error } = await supabase
        .from('household_members')
        .delete()
        .eq('household_id', household.id)
        .eq('user_id', userId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, invite, changeRole, remove }
}
